import {Trans} from '@lingui/macro';
import noop from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {Loader, LoaderVariantsEnum} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {LightUserThemeProvider} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {TrueAvailabilityStatusesEnum} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {SchedulingLinkStandardAvailabilityBlockModel} from '../../../../models/api/schedulingLinkModel';
import {CalendarsPanelId} from '../../../../store/panels/panelStateBase';
import {CalendarsGridContainer} from '../../../calendars/grid/calendarsGridContainer';
import {LargeCalendarsBase} from '../../../calendars/largeCalendarsBase';
import {SchedulingAvailabilityBanner} from './schedulingAvailabilityBanner';
import {
  addStandardAvailabilityBlock,
  removeStandardAvailabilityBlock,
  updateStandardAvailabilityBlock,
} from './standardAvailabilityHelpers';

/*
 * Types.
 */

export interface StandardAvailabilityCalendarPublicProps {
  panelId: CalendarsPanelId;
  renderRightPanel: Renderer;
}

interface StandardAvailabilityCalendarProps extends StandardAvailabilityCalendarPublicProps {
  hasNoCalendars: boolean;
  shouldCalendarDisplayWeekends: boolean;
  focusedDate: number;
  onFocusedDateSelect: HandlerOf<number>;
  selectedSlots: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>;
  setSelectedSlots: HandlerOf<ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>>;
  meetingDuration: number;
  trueAvailabilityStatus: TrueAvailabilityStatusesEnum;
  status: AsyncStatusesEnum;
}

/*
 * Style.
 */

const StyledLeftPanelDiv = styled.div`
  padding: 20px 0px;
  overflow-y: scroll;
  background-color: ${(p) => p.theme.greys.shade10};
`;

const StyledStatusDiv = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

/*
 * Component.
 */

export const StandardAvailabilityCalendar: FC<StandardAvailabilityCalendarProps> = (props) => {
  const {selectedSlots, status, renderRightPanel, setSelectedSlots, meetingDuration} = props;

  const slotDefinitions = useMemo(
    () =>
      selectedSlots.map((slot, index) => ({
        start: DateTime.fromMillis(slot.start),
        end: DateTime.fromMillis(slot.end),
        id: index,
      })),
    [selectedSlots],
  );

  if (status !== AsyncStatusesEnum.LOADED) {
    return renderAsyncStatus(props);
  }

  const renderGrid = () => (
    <CalendarsGridContainer
      panelId={props.panelId}
      isVisible
      popoverCalendarEventData={undefined}
      onEventClick={noop}
      canCreateEventOnSelect
      shouldCalendarDisplayWeekends={props.shouldCalendarDisplayWeekends}
      showHoverPlaceholder
      hoverPlaceholderDuration={meetingDuration}
      selectedSlots={slotDefinitions}
      onCalendarSlotSelect={(slot) =>
        setSelectedSlots(
          addStandardAvailabilityBlock(
            selectedSlots,
            {
              start: slot.startDateTime.valueOf(),
              end: slot.endDateTime.valueOf(),
            },
            meetingDuration,
          ),
        )
      }
      onSchedulingSlotDelete={(slotId) =>
        setSelectedSlots(removeStandardAvailabilityBlock(selectedSlots, selectedSlots[slotId]))
      }
      onSchedulingSlotUpdate={(slotId, newSlot) =>
        setSelectedSlots(
          updateStandardAvailabilityBlock(
            selectedSlots,
            selectedSlots[slotId],
            {
              start: newSlot.startDateTime.valueOf(),
              end: newSlot.endDateTime.valueOf(),
            },
            meetingDuration,
          ),
        )
      }
    />
  );

  return (
    <>
      {/* TODO: Remove theme provider when we fully support dark mode in calendar. */}
      <LightUserThemeProvider>
        <LargeCalendarsBase
          panelId={props.panelId}
          renderLeftPanel={renderLeftPanel}
          renderRightPanel={renderRightPanel}
          renderPopover={undefined}
          renderGrid={renderGrid}
          hasNoCalendars={props.hasNoCalendars}
          onGridMouseDown={noop}
          focusedDate={props.focusedDate}
          onFocusedDateSelect={props.onFocusedDateSelect}
          leftPanelWidth={240}
          onLeftPanelWidthChange={noop}
          shouldCombineCalendarsList
          shouldSaveCalendarVersion={false}
        />
      </LightUserThemeProvider>
      <SchedulingAvailabilityBanner availabilityStatus={props.trueAvailabilityStatus} />
    </>
  );
};

/*
 * Helpers.
 */

function renderLeftPanel(_width: number, content: ReactNode): ReactNode {
  return <StyledLeftPanelDiv>{content}</StyledLeftPanelDiv>;
}

function renderAsyncStatus(props: StandardAvailabilityCalendarProps) {
  if (props.status === AsyncStatusesEnum.LOADING) {
    return (
      <StyledStatusDiv>
        <Loader size={VisualSizesEnum.LARGE} variant={LoaderVariantsEnum.BASE} isEnabled />
      </StyledStatusDiv>
    );
  }

  return (
    <StyledStatusDiv>
      <Trans id="scheduling-insertmeetingtimes-loaderror">Unable to load scheduling calendar.</Trans>
    </StyledStatusDiv>
  );
}
